import React, {
  createContext,
  useCallback,
  useContext,
  useState,
  ReactNode,
} from 'react';
import PersonalWaConnectModal from '../components/personal-wa/PersonalWaConnectModal';
import PersonalWaSessionWatcher from '../components/personal-wa/PersonalWaSessionWatcher';
import { useWhatsApp } from './WhatsAppContext';

interface ConnectModalOptions {
  initialStatus?: string;
  initialError?: string | null;
  onConnected?: () => void | Promise<void>;
  onDisconnected?: () => void | Promise<void>;
}

interface PersonalWaSessionContextType {
  openConnectModal: (configId: string, options?: ConnectModalOptions) => void;
  closeConnectModal: () => void;
}

const PersonalWaSessionContext = createContext<PersonalWaSessionContextType | undefined>(
  undefined,
);

interface PersonalWaSessionProviderProps {
  children: ReactNode;
}

export const PersonalWaSessionProvider: React.FC<PersonalWaSessionProviderProps> = ({
  children,
}) => {
  const { refetchWhatsAppNumbers } = useWhatsApp();
  const [modalState, setModalState] = useState<{
    open: boolean;
    configId: string | null;
    initialStatus: string;
    initialError: string | null;
    onConnected?: () => void | Promise<void>;
    onDisconnected?: () => void | Promise<void>;
  }>({
    open: false,
    configId: null,
    initialStatus: 'disconnected',
    initialError: null,
  });

  const openConnectModal = useCallback(
    (configId: string, options?: ConnectModalOptions) => {
      setModalState({
        open: true,
        configId,
        initialStatus: options?.initialStatus || 'disconnected',
        initialError: options?.initialError ?? null,
        onConnected: options?.onConnected,
        onDisconnected: options?.onDisconnected,
      });
    },
    [],
  );

  const closeConnectModal = useCallback(() => {
    setModalState((prev) => ({
      ...prev,
      open: false,
    }));
  }, []);

  const handleDisconnect = useCallback(
    (configId: string, status: string, hint: string | null) => {
      openConnectModal(configId, {
        initialStatus: status,
        initialError: hint,
      });
    },
    [openConnectModal],
  );

  const handleReconnect = useCallback(
    (configId: string) => {
      setModalState((prev) => {
        if (prev.open && prev.configId === configId) {
          return { ...prev, open: false };
        }
        return prev;
      });
      refetchWhatsAppNumbers();
    },
    [refetchWhatsAppNumbers],
  );

  const handleConnected = useCallback(async () => {
    await refetchWhatsAppNumbers();
    await modalState.onConnected?.();
    closeConnectModal();
  }, [refetchWhatsAppNumbers, closeConnectModal, modalState.onConnected]);

  const handleDisconnected = useCallback(async () => {
    await refetchWhatsAppNumbers();
    await modalState.onDisconnected?.();
  }, [refetchWhatsAppNumbers, modalState.onDisconnected]);

  return (
    <PersonalWaSessionContext.Provider
      value={{ openConnectModal, closeConnectModal }}
    >
      {children}
      <PersonalWaSessionWatcher
        openConfigId={modalState.open ? modalState.configId : null}
        onDisconnect={handleDisconnect}
        onReconnect={handleReconnect}
      />
      <PersonalWaConnectModal
        open={modalState.open}
        configId={modalState.configId}
        initialStatus={modalState.initialStatus}
        initialError={modalState.initialError}
        onClose={closeConnectModal}
        onConnected={handleConnected}
        onDisconnected={handleDisconnected}
      />
    </PersonalWaSessionContext.Provider>
  );
};

export const usePersonalWaSession = (): PersonalWaSessionContextType => {
  const context = useContext(PersonalWaSessionContext);
  if (!context) {
    throw new Error('usePersonalWaSession must be used within PersonalWaSessionProvider');
  }
  return context;
};
